import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthProvider } from "./components/AuthContext";
import EditProfile from "./components/EditProfile";
import Home from "./components/Home";
import Info from "./components/Info";
import Logout from "./components/Logout";
import Profile from "./components/Profile";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import RecoverPassword from "./components/RecoverPassword";
import Results from "./components/Results";
import Signup from "./components/Signup";
import Signin from "./components/Signin";
import Contacts from "./components/Contacts";
import PrivacyPolicy from "./components/PrivacyPolicy";
import CookiePolicy from "./components/CookiePolicy";
import LegalDocument from './components/LegalDocument';
import LegalNotices from './components/LegalNotices';
import { COOKIE_PREFERENCES_EVENT, CONSENT_CHANGE_EVENT, getConsent, isConsentValid } from "./components/utils/cookieConsent";
import CookieBanner from "./components/CookieBanner";
import Roma from "./components/cities/Roma";
import Footer from "./components/partials/Footer";
import Favorites from "./components/Favorites";
import { FavoritesProvider } from "./components/FavoritesContext";
import NotFound from "./components/NotFound";
import { ADMIN_PATH } from './components/admin/adminConfig';

const AdminArea = lazy(() => import('./components/admin/AdminArea'));
const QuoteRequest = lazy(() => import('./components/quotes/QuoteRequest'));
const CustomerQuotes = lazy(() => import('./components/quotes/CustomerQuotes'));
const AgencyQuotes = lazy(() => import('./components/quotes/AgencyQuotes'));

function PublicFooter() {
    const { pathname } = useLocation();
    return pathname === ADMIN_PATH || pathname.startsWith(`${ADMIN_PATH}/`) ? null : <Footer />;
}

function ScrollToHashElement() {
    const location = useLocation();

    useLayoutEffect(() => {
        if (location.hash) {
            let frame;
            let id = location.hash.slice(1);
            try { id = decodeURIComponent(id); } catch { /* Keep malformed hashes harmless. */ }
            const scrollToTarget = () => {
                const elemento = document.getElementById(id);
                if (elemento) {
                    observer.disconnect();
                    frame = window.requestAnimationFrame(() => {
                        elemento.scrollIntoView({
                            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                            block: 'start',
                        });
                    });
                }
            };
            // Public routes may finish checking authentication after this effect.
            const observer = new MutationObserver(scrollToTarget);
            observer.observe(document.getElementById('root'), { childList: true, subtree: true });
            scrollToTarget();
            return () => {
                observer.disconnect();
                window.cancelAnimationFrame(frame);
            };
        }

        // The home page uses smooth scrolling for anchors. Override it while
        // entering another route so the previous animation cannot continue.
        const root = document.documentElement;
        const previousBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(0, 0);
        const frame = window.requestAnimationFrame(() => { root.style.scrollBehavior = previousBehavior; });
        return () => {
            window.cancelAnimationFrame(frame);
            root.style.scrollBehavior = previousBehavior;
        };
    }, [location.pathname, location.hash, location.key]);

    return null;
}

export default function App() {
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        const openPreferences = () => setShowBanner(true);
        const syncConsent = () => setShowBanner(!isConsentValid(getConsent()));
        syncConsent();
        window.addEventListener(COOKIE_PREFERENCES_EVENT, openPreferences);
        window.addEventListener(CONSENT_CHANGE_EVENT, syncConsent);
        return () => {
            window.removeEventListener(COOKIE_PREFERENCES_EVENT, openPreferences);
            window.removeEventListener(CONSENT_CHANGE_EVENT, syncConsent);
        };
    }, []);

    return (
        <AuthProvider>
            <FavoritesProvider>
            <BrowserRouter>
                <ScrollToHashElement />
                <div className="ef-site-shell">
                <Routes>
                    <Route path={`${ADMIN_PATH}/*`} element={<Suspense fallback={<p role="status">Caricamento…</p>}><AdminArea /></Suspense>} />
                    <Route path="/" element={
                        <PublicRoute>
                            <Home />
                        </PublicRoute>
                    } />
                    <Route path="/risultati" element={
                        <PublicRoute>
                            <Results />
                        </PublicRoute>
                    } />
                    <Route path="/preferiti" element={
                        <PublicRoute>
                            <Favorites />
                        </PublicRoute>
                    } />
                    <Route path="/info/:publicId" element={
                        <PublicRoute>
                            <Info />
                        </PublicRoute>
                    } />
                    <Route path="/registrazione" element={
                        <PublicRoute>
                            <Signup />
                        </PublicRoute>
                    } />
                    <Route path="/login" element={
                        <PublicRoute>
                            <Signin />
                        </PublicRoute>
                    } />
                    <Route path="/recupera-password" element={
                        <PublicRoute>
                            <RecoverPassword />
                        </PublicRoute>
                    } />
                    <Route path="/modifica-profilo" element={
                        <ProtectedRoute>
                            <EditProfile />
                        </ProtectedRoute>
                    } />
                    <Route path="/profilo" element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    } />
                    <Route path="/contatti" element={
                        <ProtectedRoute>
                            <Contacts />
                        </ProtectedRoute>
                    } />
                    <Route path="/logout" element={<Logout />} />
                    <Route path="/richiedi-preventivo" element={<Suspense fallback={<p role="status">Caricamento…</p>}><QuoteRequest /></Suspense>} />
                    <Route path="/preventivi/cliente" element={<Suspense fallback={<p role="status">Caricamento…</p>}><CustomerQuotes /></Suspense>} />
                    <Route path="/preventivi" element={<ProtectedRoute><Suspense fallback={<p role="status">Caricamento…</p>}><AgencyQuotes /></Suspense></ProtectedRoute>} />
                    <Route path="/preventivi/:requestId" element={<ProtectedRoute><Suspense fallback={<p role="status">Caricamento…</p>}><AgencyQuotes /></Suspense></ProtectedRoute>} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/condizioni-agenzie" element={<LegalDocument kind="terms" />} />
                    <Route path="/informazioni-legali" element={<LegalNotices />} />

                    <Route path="/migliori-onoranze-funebri-roma" element={<PublicRoute><Roma /></PublicRoute>} />
                    <Route path="/404" element={<NotFound />} />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
                <PublicFooter />
                </div>
            </BrowserRouter>
            </FavoritesProvider>

            {showBanner && (
                <CookieBanner onClose={() => setShowBanner(false)} />
            )}
        </AuthProvider>
    );
}
